// Seed a new task's composer with a draft message and/or attachments, so an
// external entry point (Deck Studio intake, Revise-a-deliverable) can drop the
// user into a primed thread. The composer hydrates these on first load
// (one-shot; cleared on read). Set `autoSubmit` to fire the first turn
// automatically once the draft + attachments have landed; leave it off when the
// user should type their own first message.

export type SeedAttachment = {
  file_id: string;
  filename: string;
  mime_type?: string;
};

export function seedTaskDraft(
  taskId: string,
  seed: string,
  attachments: SeedAttachment[],
  {
    autoSubmit = true,
    source,
  }: { autoSubmit?: boolean; source?: string } = {},
): void {
  try {
    localStorage.setItem(`railyard_draft_${taskId}`, seed);
    localStorage.setItem(
      `railyard_draft_attachments_${taskId}`,
      JSON.stringify(attachments),
    );
    // Store the autosubmit flag AS the analytics source for the first message,
    // so the shared autosubmit handler can label it accurately (Deck Studio
    // intake vs Run Skill vs Run Deck) instead of hard-coding one source.
    if (autoSubmit) {
      localStorage.setItem(`railyard_autosubmit_${taskId}`, source ?? "autosubmit");
    } else {
      // The user sends this one themselves. A reused scratch task may still
      // carry an abandoned Run Skill / Run Deck autosubmit flag; drop it so the
      // manual seed can never fire on its own (ENG-5231's no-autosubmit rule).
      localStorage.removeItem(`railyard_autosubmit_${taskId}`);
      // The send path labels that first message with the seed's source once.
      if (source) localStorage.setItem(seedSourceKey(taskId), source);
    }
  } catch {
    /* localStorage unavailable — composer just opens with the draft */
  }
}

function seedSourceKey(taskId: string): string {
  return `railyard_seed_source_${taskId}`;
}

/** Read and clear the analytics source a non-autosubmit seed left for the
 * task's first user-sent message; null when the draft was not seeded. */
export function consumeSeedSource(taskId: string): string | null {
  try {
    const key = seedSourceKey(taskId);
    const source = localStorage.getItem(key);
    if (source) localStorage.removeItem(key);
    return source || null;
  } catch {
    return null;
  }
}
